/**
 * Restaurant API Routing Utilities
 * Determines which API endpoint to call based on URL structure
 */

import {
  isTemplateDomain,
  getParentRestaurantIdFromUrl,
  getParentRestaurantIdFromQuery,
  getAppDomain,
  getFranchiseIdFromPreviewUrl,
  getRestaurantIdFromQuery,
  getFranchiseIdFromSlugPath,
  getRestaurantIdFromPaymentSuccessUrl,
  getRestaurantIdFromPaymentFailedUrl,
} from "./domainUtils";

import ENDPOINTS from "../api/endpoints";

export interface ApiCallConfig {
  fetchType: "franchise" | "parent" | "domain";
  url: string;
  headers?: Record<string, string>;
}

export const determineRestaurantApiCall = (): ApiCallConfig => {
  const isTemplate = isTemplateDomain();
  const appDomain = getAppDomain();

  // ==================================
  // Parent Restaurant API
  // /parent-restaurants/{parentId}
  // ==================================

  const parentRestaurantId =
    getParentRestaurantIdFromUrl() || getParentRestaurantIdFromQuery();

  if (parentRestaurantId) {
    return {
      fetchType: "parent",
      url: `${ENDPOINTS.API_V2_BASE_URL}${ENDPOINTS.FRANCHISE_BY_PARENT_API_PATH}`,
      headers: {
        "parent-restaurant-id": parentRestaurantId,
      },
    };
  }

  // ==================================
  // Franchise Restaurant API
  // /restaurantId
  // /slug-restaurantId
  // /restaurantId/preview/edit
  // ?restaurantId=id
  // ==================================

  const franchiseId =
  getFranchiseIdFromPreviewUrl() ||
  getRestaurantIdFromQuery() ||
  getFranchiseIdFromSlugPath() ||
  getRestaurantIdFromPaymentSuccessUrl() ||
  getRestaurantIdFromPaymentFailedUrl();

  if (franchiseId) {
    return {
      fetchType: "franchise",
      url: `${ENDPOINTS.API_V2_BASE_URL}${ENDPOINTS.FRANCHISE_API_PATH}`,
      headers: {
        "restaurant-id": franchiseId,
      },
    };
  }

  // ==================================
  // Template Domain API
  // ==================================

  if (isTemplate) {
    return {
      fetchType: "domain",
      url: `${ENDPOINTS.API_V2_BASE_URL}${ENDPOINTS.FRANCHISE_DOMAIN_API_PATH}?domain=${appDomain}`,
    };
  }

  // ==================================
  // Custom Domain API
  // ==================================

  return {
    fetchType: "domain",
    url: `${ENDPOINTS.API_V2_BASE_URL}${ENDPOINTS.FRANCHISE_DOMAIN_API_PATH}?domain=${appDomain}`,
  };
};
